import React, { useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { format } from "date-fns";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CirclePlusIcon, ExternalLinkIcon, TrashIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip } from "@/components/badge/chip";
import { ChannelStateChip } from "@/components/badge/channel-state";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { delFetcher, mutateRequest, postFetcher, putFetcher, swrKey } from "@/lib/swr";
import { useTabParam } from "@/hooks/tab-param";
import { ChannelMatrix } from "./channel-matrix";

export interface ChannelRow {
  id: number;
  pid: string;
  name: string;
  packageName?: string | null;
  /** App name as operated on Google Play (differs from the internal codename). */
  playName?: string | null;
  /** Flavor id from the pre-DB channel system (flavors.json). */
  flavorPid?: string | null;
  /** feedMode flag from the build flavor config (0/1). */
  feedMode?: number | null;
  /** Build-config's own version (independent numbering, reference only). */
  flavorVersionCode?: string | null;
  flavorVersionName?: string | null;
  latestRelease?: {
    id: number;
    versionId: string;
    releasedAt: string;
    /** Staged-rollout percent (1–100) of the latest release. */
    percent?: number | null;
    /** Channel-flow node of the latest record (the version's walk step). */
    state?: string | null;
    operatorName?: string;
    version?: {
      id: string;
      versionName?: string;
      versionCode?: string;
      state?: number;
    };
  };
}

export const playStoreUrl = (packageName: string) =>
  `https://play.google.com/store/apps/details?id=${encodeURIComponent(packageName)}`;

// Both caches that show the manifest: this page's overview + the raw list
// (tab count / version-detail Channels).
const refreshChannels = () => {
  mutateRequest("/channels/overview");
  mutateRequest("/channels");
};

// Shared create/edit form. The Google Play link is derived from the package
// name; the build config's own version fields are import-maintained
// (scripts/import-flavors.mjs), not edited here.
export const ChannelFormDialog = ({
  pid,
  channel,
  trigger,
  onSaved,
}: {
  pid: string;
  /** Present = edit mode. */
  channel?: ChannelRow;
  trigger: React.ReactNode;
  onSaved?: () => void;
}) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(channel?.name ?? "");
  const [playName, setPlayName] = useState(channel?.playName ?? "");
  const [packageName, setPackageName] = useState(channel?.packageName ?? "");
  const [flavorPid, setFlavorPid] = useState(channel?.flavorPid ?? "");
  const [feedMode, setFeedMode] = useState(!!channel?.feedMode);
  const editing = !!channel;

  const onError = (error: unknown) =>
    toastError(
      toast,
      editing ? "更新渠道失败" : "添加渠道失败",
      error
    );
  const onSuccess = () => {
    setOpen(false);
    refreshChannels();
    onSaved?.();
  };

  const { trigger: add, isMutating: adding } = useSWRMutation(
    "/channels",
    postFetcher,
    { onSuccess, onError }
  );
  const { trigger: save, isMutating: saving } = useSWRMutation(
    `/channels/${channel?.id}`,
    putFetcher,
    { onSuccess, onError }
  );
  const isMutating = adding || saving;

  const onOpenChange = (o: boolean) => {
    if (o) {
      setName(channel?.name ?? "");
      setPlayName(channel?.playName ?? "");
      setPackageName(channel?.packageName ?? "");
      setFlavorPid(channel?.flavorPid ?? "");
      setFeedMode(!!channel?.feedMode);
    }
    setOpen(o);
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || isMutating) return;
    const payload = {
      name: trimmed,
      playName: playName.trim(),
      packageName: packageName.trim(),
      flavorPid: flavorPid.trim(),
      feedMode,
    };
    if (editing) save(payload);
    else add({ pid, ...payload });
  };

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{editing ? "编辑渠道" : "添加渠道"}</DialogTitle>
          <DialogDescription>
            该 App 每次发布要出的渠道包（如某个应用商店）。Google Play
            链接由包名推导。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">名称</span>
            <Input
              value={name}
              placeholder="渠道名称…"
              autoFocus
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              onKeyDown={onEnter}
              className="h-8"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">
              Google Play 应用名（可选）
            </span>
            <Input
              value={playName}
              placeholder="商店展示名…"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPlayName(e.target.value)
              }
              onKeyDown={onEnter}
              className="h-8"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">
              包名（可选）
            </span>
            <Input
              value={packageName}
              placeholder="com.example.app"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPackageName(e.target.value)
              }
              onKeyDown={onEnter}
              className="h-8 font-mono text-xs"
            />
          </label>
          <div className="flex items-end gap-4">
            <label className="block flex-1 space-y-1">
              <span className="text-sm text-muted-foreground">
                Flavor pid（可选）
              </span>
              <Input
                value={flavorPid}
                placeholder="e.g. 7"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFlavorPid(e.target.value)
                }
                onKeyDown={onEnter}
                className="h-8"
              />
            </label>
            <label className="flex h-8 items-center gap-2 text-sm">
              <Checkbox
                checked={feedMode}
                onCheckedChange={(v: boolean) => setFeedMode(!!v)}
              />
              feedMode
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            取消
          </Button>
          <Button size="sm" disabled={isMutating || !name.trim()} onClick={submit}>
            {editing ? "保存" : "添加"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Channels with release records can't be removed — the server rejects and we
// surface its message.
export const DeleteChannelButton = ({
  channel,
  onDeleted,
}: {
  channel: ChannelRow;
  onDeleted?: () => void;
}) => {
  const { toast } = useToast();
  const { trigger: remove } = useSWRMutation("/channels", delFetcher, {
    onSuccess: () => {
      refreshChannels();
      onDeleted?.();
    },
    onError: (error: unknown) =>
      toastError(toast, "删除渠道失败", error),
  });
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => remove(String(channel.id))}
      title={`删除 ${channel.name}`}
      aria-label={`删除 ${channel.name}`}
      className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
    >
      <TrashIcon className="h-3.5 w-3.5" />
    </Button>
  );
};

// Compact Google Play link (icon only) — the raw package name is too long for
// a row, it lives in the hover title and the edit dialog.
const PlayStoreLink = ({ packageName }: { packageName: string }) => (
  <a
    href={playStoreUrl(packageName)}
    target="_blank"
    rel="noreferrer"
    title={`Google Play · ${packageName}`}
    onClick={(e) => e.stopPropagation()}
    className="flex shrink-0 items-center text-muted-foreground hover:text-primary"
  >
    <ExternalLinkIcon className="h-3.5 w-3.5" />
  </a>
);

// Channel perspective of the app: each channel with the version it last
// shipped and when; falls back to the build config's own version when no
// system release is recorded yet. Rows are ordered by flavor pid. Click a row
// for the channel's full release history — edit/remove live there; adding is
// open to anyone who can see the app (server checks visibility).
const AppChannels = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // 两种视图:List=渠道清单(原列表);Matrix=版本×渠道状态矩阵
  const [view, setView] = useTabParam("view", "matrix");
  const { data: rows, isLoading } = useSWR<ChannelRow[]>(
    swrKey("/channels/overview", { pid: id })
  );

  // Order by flavor pid (numeric); channels without one sink to the bottom.
  const sorted = [...(rows ?? [])].sort((a, b) => {
    const pa = a.flavorPid ? Number(a.flavorPid) : Infinity;
    const pb = b.flavorPid ? Number(b.flavorPid) : Infinity;
    return pa - pb || a.id - b.id;
  });

  if (isLoading) return <ListLoading />;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="matrix">
              Matrix
            </TabsTrigger>
            <TabsTrigger value="list">
              List
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <ChannelFormDialog
          pid={String(id)}
          trigger={
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 px-2 text-xs"
            >
              <CirclePlusIcon className="h-3.5 w-3.5" /> 添加渠道
            </Button>
          }
        />
      </div>
      {view === "matrix" ? (
        <ChannelMatrix pid={String(id)} />
      ) : (
        <>
          {!rows?.length && <ListEmpty>该 App 还没有配置渠道。</ListEmpty>}
          <div className="flex flex-col gap-1">
        {sorted.map((c) => {
          const r = c.latestRelease;
          return (
            <RowCard
              key={c.id}
              onClick={() => navigate(`/apps/${c.pid}/channels/${c.id}`)}
            >
              {c.flavorPid ? (
                <RowIndex
                  n={c.flavorPid}
                  title="flavor pid (versionNameFlavorPid)"
                />
              ) : (
                <span className="w-8 shrink-0" />
              )}
              <span className="min-w-0 shrink-0 truncate font-medium">
                {c.name}
              </span>
              {c.playName && (
                <span
                  title="Google Play 上的应用名"
                  className="min-w-0 truncate text-xs text-muted-foreground"
                >
                  {c.playName}
                </span>
              )}
              {c.packageName && <PlayStoreLink packageName={c.packageName} />}
              <span className="min-w-0 flex-1" />
              {!!c.feedMode && <Chip tone="teal">feed</Chip>}
              {/* 渠道无整体状态:显示最近一条记录走到的 flow 节点 */}
              <span className="flex w-14 shrink-0 items-center">
                {r?.state && <ChannelStateChip state={r.state} />}
              </span>
              {/* Fixed slots right of the spacer so every row lines up:
                  version (left-aligned) · percent · operator · date,
                  "—"/empty when absent. */}
              <span className="flex w-44 shrink-0 items-center overflow-hidden">
                {r ? (
                  <Link
                    to={`/apps/${c.pid}/versions/${r.versionId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="max-w-full"
                  >
                    <Chip tone="blue" className="max-w-full truncate hover:underline">
                      {r.version?.versionName ?? r.versionId}
                      {r.version?.versionCode && `(${r.version.versionCode})`}
                    </Chip>
                  </Link>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    未发布过
                  </span>
                )}
              </span>
              <span
                title="放量百分比"
                className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
              >
                {r?.state === "rolling" && r?.percent != null
                  ? `${r.percent}%`
                  : "—"}
              </span>
              <span className="w-20 shrink-0 truncate text-right text-xs text-muted-foreground">
                {r?.operatorName ?? "—"}
              </span>
              <span className="w-24 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {r ? format(new Date(r.releasedAt), "yyyy-MM-dd") : "—"}
              </span>
            </RowCard>
          );
        })}
          </div>
        </>
      )}
    </div>
  );
};

export default AppChannels;
